<template>
    <!-- Metadata and Note -->
    <div v-bind:class="panelClassObj">
        <div
            v-if="!store.enableAttachmentAnalysis"
            v-bind:class="halfColClass">
            <!-- Metadata - Only shown here if the attachment analysis feature is not enabled -->
            <div>
                <div v-bind:class="metadataRowClass">
                    <div class="col-12 col-md-6">
                        <div class="fw-bold">
                            {{ fileNameLabel }}
                        </div>
                    </div>
                    <div class="col-12 col-md-6">
                        <a
                            data-gtmid="AttachmentView-DownloadOriginal"
                            v-bind:href="store.attachmentDownloadUrl">
                            {{ store.attachmentModel.FileName }}
                        </a>
                    </div>
                </div>
            </div>
            <div
                v-for="{ key, value, valueClass, valueId } in attachmentMetadata"
                v-bind:key="key">
                <div v-bind:class="metadataRowClass">
                    <div v-bind:class="halfColClass">
                        <div class="fw-bold">
                            {{ key }}
                        </div>
                    </div>
                    <div v-bind:class="halfColClass">
                        <span
                            v-bind:id="valueId"
                            v-bind:class="valueClass">
                            {{ value }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <div v-bind:class="halfColClass">
            <ai-prompt-preview-component v-if="store.isAIAssistantPreview" />
            <pc-loader
                v-bind:loading="generatingAiNote"
                message="Generating AI note...">
                <!-- Note content -->
                <pc-label
                    for-id="noteContent"
                    v-bind:label="noteInputLabel"
                    bold />
                <pc-textarea
                    id="noteContent"
                    v-model="store.noteText"
                    v-bind:disabled="!props.canApproveAttachment"
                    v-bind:maxlength="2048"
                    v-bind:show-remaining-characters="true"
                    class="mb-3"
                    v-on:update:model-value="setUnsavedChanges(true)" />
            </pc-loader>
            <!-- Buttons -->
            <div v-if="props.canApproveAttachment">
                <div
                    v-if="!store.enableAttachmentAnalysis"
                    class="d-flex align-items-center justify-content-end">
                    <pc-button
                        id="approve"
                        class="me-2"
                        data-gtmid="AttachmentView-StatusChange-Approve"
                        v-bind:disabled="isApproved"
                        v-on:click="onApprove">
                        {{ approveBtnLabel }}
                    </pc-button>
                    <pc-button
                        id="reject"
                        class="me-2"
                        data-gtmid="AttachmentView-StatusChange-Reject"
                        v-bind:disabled="isRejected"
                        v-on:click="onReject">
                        {{ rejectBtnLabel }}
                    </pc-button>
                    <pc-button
                        id="save"
                        v-bind:class="saveButtonClass"
                        data-gtmid="AttachmentView-SaveNote"
                        v-on:click="onSaveNote">
                        {{ saveBtnLabel }}
                    </pc-button>
                    <pc-button
                        v-if="!isPendingApproval"
                        id="unmark"
                        data-gtmid="AttachmentView-UnmarkStatus"
                        v-on:click="onUnmarkStatus">
                        {{ unmarkBtnLabel }}
                    </pc-button>
                </div>
                <div
                    v-else
                    class="d-flex align-items-center justify-content-end">
                    <pc-button
                        v-if="hasAiAnalysisResults"
                        id="aiNote"
                        data-gtmid="AttachmentView-GenerateNote"
                        v-bind:disabled="generatingAiNote"
                        class="me-2"
                        v-on:click="onGenerateNote">
                        Generate with AI Assistant
                    </pc-button>
                    <pc-button
                        v-if="!store.isAIAssistantPreview"
                        id="save"
                        class="me-2"
                        data-gtmid="AttachmentView-SaveNote"
                        v-on:click="onSaveNote">
                        {{ saveBtnLabel }}
                    </pc-button>
                </div>
                <div v-if="store.enableAttachmentAnalysis && !store.isAIAssistantPreview">
                    <br />
                    <div
                        v-for="{ key, value, valueClass, valueId } in reviewerMetadata"
                        v-bind:key="key">
                        <div v-bind:class="metadataRowClass">
                            <div class="col-12 col-md-6">
                                <div class="fw-bold">
                                    {{ key }}
                                </div>
                            </div>
                            <div class="col-12 col-md-6">
                                <span
                                    v-bind:id="valueId"
                                    v-bind:class="valueClass">
                                    {{ value }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <br />
                    <br />
                </div>
                <pc-loader
                    v-if="store.enableAttachmentAnalysis"
                    v-bind:loading="store.aiAnalysisRunning"
                    message="Analyzing attachment...">
                    <div class="row gy-5">
                        <div>
                            <div class="d-flex align-items-end">
                                <h2 class="flex-grow-1">AI Attachment Analysis</h2>
                                <div v-if="hasAiAnalysisResults">
                                    <pc-button-icon
                                        link
                                        icon="chat"
                                        class="p-0"
                                        data-gtmid="AttachmentView-VerbatimFeedback"
                                        v-on:click="onGiveFeedback">
                                        Give Feedback
                                    </pc-button-icon>
                                </div>
                            </div>
                            <hr />
                            <div class="d-flex align-items-end gap-3">
                                <pc-select
                                    id="promptSelector"
                                    v-model="store.promptId"
                                    class="flex-grow-1"
                                    v-bind:readonly="store.isAIAssistantPreview"
                                    label="Attachment Review Assistant"
                                    data-gtmid="AttachmentView-PromptSelector"
                                    v-bind:options="store.promptList"
                                    text-prop="DisplayName"
                                    value-prop="PromptId">
                                    <option value="">Select...</option>
                                </pc-select>
                                <pc-button
                                    id="aiAnalysis"
                                    data-gtmid="AttachmentView-RunAIAnalysis"
                                    primary
                                    v-bind:disabled="isRunDisabled()"
                                    v-on:click="runAIAnalysis">
                                    Run
                                </pc-button>
                            </div>
                            <div
                                v-if="Boolean(store.promptId) && !store.isAIAssistantPreview"
                                class="d-flex align-items-end gap-3 py-2">
                                <div class="text-body-secondary fst-italic small">
                                    Last Modified:
                                    {{
                                        store.promptList.find((pr) => pr.PromptId === store.promptId)
                                            ?.LastChangeTimestamp?.Display
                                    }}
                                </div>
                            </div>
                        </div>
                        <!-- Add some vertical space before the next section -->
                        <div>
                            <pc-transition v-bind:transition="transitionType.slide">
                                <div>
                                    <div v-if="!hasAiAnalysisResults && !store.aiAnalysisRunning">
                                        <div class="fst-italic text-muted">No attachment review has been run.</div>
                                    </div>
                                    <div class="d-flex flex-grow-1 align-items-center justify-content-between mb-2">
                                        <div
                                            v-if="hasAiAnalysisResults && !store.aiAnalysisRunning"
                                            class="align-items-start">
                                            <div>
                                                <b>Last assistant run: </b>
                                                {{ store.lastRunPrompt?.DisplayName }}
                                            </div>
                                        </div>
                                        <div
                                            v-if="hasAiAnalysisResults && !store.aiAnalysisRunning"
                                            class="d-flex align-items-center justify-content-end mb-2">
                                            <div class="text-body-secondary fst-italic small">
                                                {{ store.aiExecutionTimeString }}
                                            </div>
                                            <pc-button-icon
                                                id="aiClearResult"
                                                data-gtmid="AttachmentView-ClearAIResult"
                                                link
                                                icon="arrow-counterclockwise"
                                                v-on:click="clearAIResult">
                                                Clear Results
                                            </pc-button-icon>
                                        </div>
                                    </div>
                                    <div>
                                        <pc-alert
                                            v-bind:shown="store.resultsOlderThanPrompt"
                                            data-gtmid="AttachmentView-ResultsOlderThanPrompt"
                                            message="Results were generated by an earlier version of this Attachment Review Assistant"
                                            v-bind:severity="ALERT_SEVERITY.warning" />
                                    </div>
                                    <table
                                        v-if="store.aiSummaryResults && !store.aiAnalysisRunning"
                                        class="table table-bordered table-striped">
                                        <thead>
                                            <tr>
                                                <th>Summary</th>
                                                <th>Result</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>
                                                    {{ store.aiSummaryResults.SummaryJustification }}
                                                </td>
                                                <td>
                                                    <span
                                                        v-bind:class="
                                                            getClassForLabel(store.aiSummaryResults.SummaryResult)
                                                        ">
                                                        {{ store.aiSummaryResults.SummaryResult }}
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <table
                                        v-if="store.aiRequirementsAnalysis && !store.aiAnalysisRunning"
                                        class="table table-bordered table-striped">
                                        <thead>
                                            <tr>
                                                <th>Requirement</th>
                                                <th>Result</th>
                                                <th>Justification</th>
                                                <th
                                                    title="Click thumbs up or down to indicate if the AI's analysis for this requirement was helpful or not">
                                                    Feedback
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="(analysis, index) in store.aiRequirementsAnalysis"
                                                v-bind:key="index">
                                                <td>
                                                    {{ analysis.Text }}
                                                </td>
                                                <td>
                                                    <span
                                                        v-bind:class="getClassForLabel(analysis.Result)"
                                                        style="white-space: nowrap">
                                                        {{ analysis.Result }}
                                                    </span>
                                                </td>
                                                <td style="white-space: pre-wrap">
                                                    {{ analysis.Justification }}
                                                    <span style="font-weight: bold; font-size: 10px">
                                                        {{ expandedJustification(analysis) }}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div class="d-flex gap-3 justify-content-center">
                                                        <pc-button-icon
                                                            v-bind:icon="getThumbIcon(analysis, 'thumbs-up')"
                                                            link
                                                            title="Helpful"
                                                            class="fs-4 feedback-icon"
                                                            v-on:click="() => onThumbsUp(analysis)">
                                                        </pc-button-icon>

                                                        <pc-button-icon
                                                            v-bind:icon="getThumbIcon(analysis, 'thumbs-down')"
                                                            link
                                                            title="Not Helpful"
                                                            class="fs-4 feedback-icon"
                                                            v-on:click="() => onThumbsDown(analysis)">
                                                        </pc-button-icon>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <div
                                        v-if="
                                            store.isAIAssistantPreview &&
                                            hasAiAnalysisResults &&
                                            !store.aiAnalysisRunning &&
                                            props.cprAdmin
                                        ">
                                        <hr />
                                        <pc-textarea
                                            id="aiResultDebugJson"
                                            v-model="store.aiResultDebugJson"
                                            v-bind:rows="20"
                                            class="cpadmin-text mb-3"
                                            label="AI Result Debug JSON: (CPR Admin Only)" />
                                    </div>
                                </div>
                            </pc-transition>
                        </div>
                    </div>
                </pc-loader>
            </div>
        </div>
    </div>
    <pc-alert
        v-bind="alertBinder"
        v-model:shown="showSuccessAlert" />
</template>
<script setup>
    import { ref, computed, inject, watch } from "vue";
    import { useResource } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/attachmentMetadataComponent.i18n";
    import { formatBasicUserInfoForDisplay } from "Util/clientUser";
    import {
        getApprovalStatusDisplay,
        getApprovalStatusDisplayClass,
        ATTACHMENT_APPROVAL_STATUS,
        ATTACHMENT_STATUS_ACTION
    } from "Util/clientAttachment";
    import userFeedbackDialog, {
        HOOKS as FEEDBACK_DIALOG_HOOKS,
        RESULTS as FEEDBACK_DIALOG_RESULTS
    } from "Feedback/userFeedbackDialog.vue";
    import { TRANSITION_TYPE } from "Util/animations";
    import { useAttachmentViewStore } from "./attachmentViewStore";
    import { ALERT_SEVERITY, ALERT_DURATION } from "Util/alerts";
    import aiPromptPreviewComponent from "./aiPromptPreviewComponent.vue";
    import { PAGE_APP_SYMBOL } from "_Main/injectionSymbols";

    const page = inject(PAGE_APP_SYMBOL);
    const store = useAttachmentViewStore();

    const emit = defineEmits([
        "update-attachment-status",
        "save-feedback",
        "save-requirement-feedback",
        "run-ai-analysis",
        "clear-ai-result"
    ]);

    const {
        updateSuccessAlertText,
        fileNameLabel,
        uploadedByLabel,
        uploadTimestampLabel,
        statusLastUpdatedByLabel,
        statusLastUpdatedTimeLabel,
        approveBtnLabel,
        rejectBtnLabel,
        saveBtnLabel,
        unmarkBtnLabel,
        noteInputLabel,
        currentStatusLabel
    } = useResource(KEYS, NAMESPACE);

    // props
    const props = defineProps({
        canApproveAttachment: { type: Boolean, required: true },
        cprAdmin: { type: Boolean, required: true }
    });

    // data
    const generatingAiNote = ref(false);
    const transitionType = ref(TRANSITION_TYPE);
    const showSuccessAlert = ref(false);

    // style
    const panelClassObj = computed(() => ({
        "pe-3": store.enableAttachmentAnalysis,
        "row mb-3": !store.enableAttachmentAnalysis
    }));
    const metadataRowClass = ref("row mb-3 mb-md-1");
    const halfColClass = computed(() => (store.enableAttachmentAnalysis ? "col-12" : "col-12 col-md-6"));
    const saveButtonClass = computed(() => {
        return { "me-2": !isPendingApproval.value };
    });

    // computed
    const uploadUserDisplay = computed(() =>
        formatBasicUserInfoForDisplay(store.attachmentModel?.UploadUser, null, true)
    );
    const attachmentMetadata = computed(() => {
        const baseMetadata = [
            {
                key: uploadedByLabel.value,
                value: uploadUserDisplay.value
            },
            { key: uploadTimestampLabel.value, value: store.attachmentModel?.UploadTimestamp?.Display }
        ];

        if (!store.enableAttachmentAnalysis) {
            // Append the reviewer metadata to baseMetadata
            baseMetadata.push(...reviewerMetadata.value);
        }

        return baseMetadata;
    });
    const reviewerMetadata = computed(() => {
        const baseMetadata = [
            {
                key: currentStatusLabel.value,
                value: getApprovalStatusDisplay(store.attachmentModel?.Status),
                valueClass: getApprovalStatusDisplayClass(store.attachmentModel?.Status),
                valueId: "currentStatus"
            }
        ];

        if (store.attachmentModel.StatusChangeUser) {
            baseMetadata.push({
                key: statusLastUpdatedByLabel.value,
                value: formatBasicUserInfoForDisplay(store.attachmentModel?.StatusChangeUser, null, true)
            });
            if (store.attachmentModel?.StatusChangeTimestamp) {
                baseMetadata.push({
                    key: statusLastUpdatedTimeLabel.value,
                    value: store.attachmentModel?.StatusChangeTimestamp.Display
                });
            }
        }

        return baseMetadata;
    });
    const isApproved = computed(() => store.attachmentModel.Status === ATTACHMENT_APPROVAL_STATUS.approved);
    const isPendingApproval = computed(() => store.attachmentModel.Status === ATTACHMENT_APPROVAL_STATUS.pending);
    const isRejected = computed(() => store.attachmentModel.Status === ATTACHMENT_APPROVAL_STATUS.rejected);
    const hasAiAnalysisResults = computed(
        () => store.enableAttachmentAnalysis && (store.aiSummaryResults || store.aiRequirementsAnalysis)
    );

    // Initialize properties for each analysis when the analysis data changes
    watch(
        () => store.aiRequirementsAnalysis,
        (newAnalysis) => {
            if (newAnalysis) {
                newAnalysis.forEach((analysis) => {
                    if (analysis.userFeedback === undefined) {
                        analysis.userFeedback = null;
                    }
                });
            }
        },
        { immediate: true }
    );

    const alertBinder = computed(() => ({
        message: updateSuccessAlertText.value,
        severity: ALERT_SEVERITY.success,
        duration: ALERT_DURATION.short
    }));

    // functions
    function setUnsavedChanges(hasChanges) {
        page.setUnsavedChanges(hasChanges);
    }
    function onApprove() {
        _updateAttachmentStatus(ATTACHMENT_STATUS_ACTION.approve);
    }
    function onReject() {
        _updateAttachmentStatus(ATTACHMENT_STATUS_ACTION.reject);
    }
    function onSaveNote() {
        _updateAttachmentStatus(ATTACHMENT_STATUS_ACTION.save);
    }
    function onUnmarkStatus() {
        _updateAttachmentStatus(ATTACHMENT_STATUS_ACTION.unmark);
    }
    function _updateAttachmentStatus(statusUpdateAction) {
        emit("update-attachment-status", statusUpdateAction);
    }
    function onGenerateNote() {
        generatingAiNote.value = true;
        setTimeout(() => {
            generatingAiNote.value = false;
            store.generateAiNote();
        }, 1000); // Simulate a delay for the AI to generate the note
    }
    async function onGiveFeedback() {
        let feedback;
        try {
            feedback = await _getUserFeedback(true);
        } catch (e) {
            // User cancelled; do nothing
            return;
        }

        // Don't save empty feedback
        if (!feedback.Content) {
            return;
        }

        emit("save-feedback", feedback);
    }

    async function _getUserFeedback() {
        const { action, data } = await page.showDialog({
            title: "Give Feedback",
            customComponent: userFeedbackDialog,
            customComponentOptions: { isVoluntary: true },
            customComponentResultHook: FEEDBACK_DIALOG_HOOKS.getResultData,
            buttons: [
                {
                    text: "Cancel",
                    result: FEEDBACK_DIALOG_RESULTS.cancel
                },
                {
                    text: "Send Feedback",
                    primary: true,
                    result: FEEDBACK_DIALOG_RESULTS.ok
                }
            ]
        });

        if (action === FEEDBACK_DIALOG_RESULTS.cancel) {
            return Promise.reject("User cancelled giving feedback");
        }

        return data;
    }

    async function runAIAnalysis() {
        emit("run-ai-analysis");
    }
    async function clearAIResult() {
        emit("clear-ai-result");
    }

    function expandedJustification(analysis) {
        let referencedData = "\n";
        // Loop through the analysis.ReferencedData and add each to referenceData
        if (!analysis.ReferencedData) {
            return "";
        }

        for (let i = 0; i < analysis.ReferencedData.length; i++) {
            if (analysis.ReferencedData[i].ProjectData == "" || analysis.ReferencedData[i].ProjectData == "N/A")
                continue;
            referencedData +=
                analysis.ReferencedData[i].Label +
                ":\n" +
                "    Project Data: " +
                analysis.ReferencedData[i].ProjectData +
                "\n" +
                "    Attachment Data: " +
                analysis.ReferencedData[i].AttachmentData +
                "\n";
        }
        return referencedData;
    }

    function getClassForLabel(label) {
        switch (label) {
            case "Deficient":
            case "Unlikely Met":
                return "text-bubble-danger";
            case "Unsure":
                return "text-bubble-warning";
            case "Sufficient":
            case "Likely Met":
                return "text-bubble-success";
            default:
                return "";
        }
    }

    function getThumbIcon(analysis, iconType) {
        if (iconType === "thumbs-up") {
            return analysis.userFeedback === "helpful" ? "hand-thumbs-up-fill" : "hand-thumbs-up";
        } else if (iconType === "thumbs-down") {
            return analysis.userFeedback === "not-helpful" ? "hand-thumbs-down-fill" : "hand-thumbs-down";
        }
        return "";
    }
    function onThumbsUp(analysis) {
        // Toggle feedback status
        if (analysis.userFeedback === "helpful") {
            // If already selected, unselect it
            analysis.userFeedback = null;
        } else {
            // Set to helpful
            analysis.userFeedback = "helpful";
        }

        // Emit the event to the parent component
        emit("save-requirement-feedback", analysis);
    }
    function onThumbsDown(analysis) {
        // Toggle feedback status
        if (analysis.userFeedback === "not-helpful") {
            // If already selected, unselect it
            analysis.userFeedback = null;
        } else {
            // Set to not helpful
            analysis.userFeedback = "not-helpful";
        }

        // Emit the event to the parent component
        emit("save-requirement-feedback", analysis);
    }

    function isRunDisabled() {
        return !store.promptId || !store.hasAttachment;
    }
</script>

<style scoped>
    .feedback-icon {
        transition: all 0.2s ease;
        transform: scale(1);
        padding: 5px;
    }
    .feedback-icon:hover {
        transform: scale(1.25);
    }
    .cpadmin-text {
        color: var(--pc-cp-admin-color);
    }
</style>
